import { jwtDecode } from "jwt-decode";
import axios from "./axios-config";
import { AUTH_API_BASE } from "./axios-config";
import type { User } from "@/features/user/types/user";
// 刻意 import 这个零运行时依赖的小模块，而不是 notification-inbox-web-component：
// 后者会把 @novu/* + Radix + lucide 拖进 @/api barrel（~270 个消费者）的模块图。
import { clearNotificationInboxCache } from "@/components/notifications/notification-inbox-cache";
// 同理：零依赖小模块（F3 阶段 4），不把聊天模块图拖进 @/api
import { clearAllChatSnapshots } from "@/features/ai-chat/chatSessionClear";

let refreshInFlight: Promise<string> | null = null;

// 认证相关类型定义
export interface Credentials {
    username: string;
    password: string;
}

export interface TokenResponse {
    access_token: string;
    refresh_token: string;
    expires_in: number; // seconds
}

/**
 * `POST /system/auth/token/refresh` 返回的 token dict（snake_case——后端
 * JWTOutSchema.model_dump() 不带 by_alias）。axios-config 拦截器已把
 * SuccessResponse 信封 { code, msg, data } 解包为 data 部分，所以调用方拿到的
 * 就是这个形状。
 */
export interface TokenRefreshResponse {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
}

export interface LoginResult {
    success: boolean;
    access_token: string;
}

// Auth API
export const authApi = {
    login: (credentials: Credentials): Promise<LoginResult> => {
        // 仅非 prod 打印（含明文密码，绝不能进生产 console）
        if (import.meta.env.MODE !== 'prod') console.log("Login credentials:", credentials);
        const formData = new FormData();
        formData.append("username", credentials.username);
        formData.append("password", credentials.password);

        return axios
            .post<any>(`${AUTH_API_BASE}/system/auth/login`, formData, {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            })
            .then((response) => {
                // axios-config 拦截器已把信封解包为 data 部分，运行时拿到的就是 token dict
                const { access_token, refresh_token } = response as unknown as TokenResponse;

                // 存储token
                localStorage.setItem("ACCESS_TOKEN", access_token);
                localStorage.setItem("REFRESH_TOKEN", refresh_token);

                // 设置Authorization头
                axios.defaults.headers.common["Authorization"] =
                    `Bearer ${access_token}`;

                return { success: true, access_token };
            });
    },

    logout: async (): Promise<void> => {
        console.log('🚪 authApi.logout: 开始退出登录流程...')

        // 1. 获取 token（优先 Clerk token，其次传统 JWT token）
        let token: string | null = null
        let isClerkUser = false

        // 尝试获取 Clerk token
        try {
            // @ts-ignore - __getClerkToken 由 AuthContext 提供
            if (window.__getClerkToken) {
                // @ts-ignore
                token = await window.__getClerkToken()
                if (token) {
                    isClerkUser = true
                    console.log('✅ authApi.logout: 使用 Clerk token')
                }
            }
        } catch (error) {
            console.error('❌ authApi.logout: 获取 Clerk token 失败:', error)
            // 标记为 Clerk 用户（即使获取失败），后续需要特殊处理
            // @ts-ignore
            if (window.__getClerkToken) {
                isClerkUser = true
            }
        }

        // 如果没有 Clerk token，尝试获取传统 JWT token
        if (!token) {
            token = localStorage.getItem("ACCESS_TOKEN")
            if (token) {
                console.log('✅ authApi.logout: 使用传统 JWT token')
            } else if (!isClerkUser) {
                console.warn('⚠️ authApi.logout: 未找到任何 token')
            }
        }

        // 2. 先调用后端登出 API（修复时序问题）
        let apiCallSuccess = false
        try {
            if (token) {
                console.log('🌐 authApi.logout: 调用后端 logout API...')
                await axios.post(
                    `${AUTH_API_BASE}/system/auth/logout`,
                    { token },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    },
                )
                console.log('✅ authApi.logout: 后端 API 调用成功，服务器端 session 已清除')
                apiCallSuccess = true
            } else {
                // 没有任何 token 时直接跳过后端 API，不做无 token 硬调——后端
                // LogoutPayloadSchema 的 token 必填(min_length=1)，空 body 必 422，
                // 白等一个往返还刷一条报错。Clerk 用户 token 获取失败时，session
                // 销毁交给随后的 clerk.signOut()，本地清理（localStorage / Novu
                // 缓存 / 菜单权限）照常进行，无实际损失。
                console.warn(
                    isClerkUser
                        ? '⚠️ authApi.logout: Clerk token 获取失败，跳过后端 API（session 由 clerk.signOut() 销毁）'
                        : '⚠️ authApi.logout: 没有找到任何 token，跳过后端 API 调用'
                )
            }
        } catch (error) {
            console.error(
                "❌ authApi.logout: 后端 API 调用失败:",
                error,
            )
            console.warn("⚠️ 服务器端 session 可能未被清除，但继续本地登出")
        }

        // 3. 清除本地存储（无论 API 调用是否成功）
        localStorage.removeItem("ACCESS_TOKEN")
        localStorage.removeItem("REFRESH_TOKEN")
        console.log('✅ authApi.logout: 本地存储已清除')

        // 4. 清除 Novu Notification Inbox 缓存
        clearNotificationInboxCache()
        console.log('✅ authApi.logout: Novu 缓存已清除')

        // 4b. 清除 AI 助手的刷新恢复快照（sessionStorage 里的聊天内容；还开着的浮窗会跟着关）
        clearAllChatSnapshots()

        // 5. 记录最终状态
        if (!apiCallSuccess && token) {
            console.error('🚨 authApi.logout: 警告 - 本地登出完成，但服务器端 session 可能仍然有效！')
        }
    },

    register: (userData: {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
        mobile?: string;
        organizationType: "create" | "join";
        accessCode?: string;
        companyName?: string;
        companyEmail?: string;
        contactNumber?: string;
    }): Promise<{ email: string; password: string }> => {
        const requestData: Record<string, any> = {
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            password: userData.password,
        };

        // Add mobile if provided
        if (userData.mobile) {
            requestData.mobile = userData.mobile;
        }

        // Handle organization type
        if (userData.organizationType === "join") {
            requestData.accessCode = userData.accessCode;
        } else if (userData.organizationType === "create") {
            requestData.companyName = userData.companyName;
            if (userData.companyEmail) {
                requestData.companyEmail = userData.companyEmail;
            }
            if (userData.contactNumber) {
                requestData.contactNumber = userData.contactNumber;
            }
        }

        // axios-config 拦截器已解包信封，运行时返回的就是 data 部分
        return axios.post<{ email: string; password: string }>(
            `${AUTH_API_BASE}/system/auth/register`,
            requestData,
            {
                headers: { "Content-Type": "application/json" },
            },
        ) as unknown as Promise<{ email: string; password: string }>;
    },

    resetPassword: (userData: {
        username: string;
        mobile: string;
        newPassword: string;
    }): Promise<User> => {
        const requestData = {
            username: userData.username,
            mobile: userData.mobile,
            new_password: userData.newPassword,
        };

        // axios-config 拦截器已解包信封，运行时返回的就是 data 部分
        return axios.post<User>(
            `${AUTH_API_BASE}/system/user/forget/password`,
            requestData,
            {
                headers: { "Content-Type": "application/json" },
            },
        ) as unknown as Promise<User>;
    },

    refreshToken: (): Promise<string> => {
        if (refreshInFlight) return refreshInFlight;
        const requestedRefresh = localStorage.getItem("REFRESH_TOKEN");
        const refresh = async (): Promise<string> => {
            const refreshToken = localStorage.getItem("REFRESH_TOKEN");
            if (!refreshToken) throw new Error("No refresh token stored");
            if (refreshToken !== requestedRefresh) {
                const session = (value: string | null) => {
                    try {
                        const claims = jwtDecode<{ sub: string }>(value ?? "");
                        const identity = JSON.parse(claims.sub);
                        return identity.session_id && identity.user_id
                            ? JSON.stringify([identity.session_id, identity.user_id]) : null;
                    } catch { return null; }
                };
                const originalSession = session(requestedRefresh);
                if (!originalSession || session(refreshToken) !== originalSession) {
                    throw new Error("Session changed during refresh");
                }
                const access = localStorage.getItem("ACCESS_TOKEN");
                if (!access) throw new Error("Session changed during refresh");
                return access;
            }

            // axios-config 拦截器解包信封后返回 data 部分（snake_case token dict）。
            // 旧代码读 response.accessToken（camelCase）恒 undefined——刷新"成功"却把
            // "undefined" 存进 ACCESS_TOKEN，WebView ticket 模式的 401 恢复实际从未
            // 工作过。兼容 camelCase 仅作防御。
            const response = await axios.post<TokenRefreshResponse>(
                `${AUTH_API_BASE}/system/auth/token/refresh`,
                { refresh_token: refreshToken },
            ) as unknown as TokenRefreshResponse & { accessToken?: string; refreshToken?: string };

            const accessToken = response.access_token ?? response.accessToken;
            if (!accessToken) {
                throw new Error("No access token in refresh response");
            }
            if (localStorage.getItem("REFRESH_TOKEN") !== refreshToken) {
                throw new Error("Session changed during refresh");
            }
            localStorage.setItem("ACCESS_TOKEN", accessToken);

            // 后端每次刷新都轮换 refresh_token——必须存回新值，否则旧 refresh_token
            // 在下一次刷新时被服务端精确比对拒绝（防重放），7 天窗口名存实亡。
            const rotatedRefresh = response.refresh_token ?? response.refreshToken;
            if (rotatedRefresh) {
                localStorage.setItem("REFRESH_TOKEN", rotatedRefresh);
            }
            return accessToken;
        };
        // Rotating refresh credentials are shared by tabs. Serialize across tabs
        // where Web Locks is available; same-tab coalescing remains universal.
        refreshInFlight = (navigator.locks
            ? navigator.locks.request("nova-session-refresh", refresh)
            : refresh()).finally(() => { refreshInFlight = null; });
        return refreshInFlight;
    },

    /**
     * Exchange a one-time auth ticket for JWT tokens.
     * Used by the ticket-login page for WebView authentication.
     * Uses native fetch() to avoid global axios interceptors that auto-unwrap responses.
     */
    exchangeTicket: async (ticket: string): Promise<{
        access_token: string;
        refresh_token: string;
        token_type: string;
        expires_in: number;
    }> => {
        const res = await fetch(`${AUTH_API_BASE}/system/auth/ticket/exchange`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ticket }),
        });

        if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.msg || errData.message || `Exchange failed: ${res.status}`);
        }

        const body = await res.json();
        // Backend wraps in { code, data, msg } — extract inner data
        return body.data || body;
    },

    hasValidToken: (): boolean => {
        const token = localStorage.getItem("ACCESS_TOKEN");
        if (!token) return false;

        try {
            const decoded: { exp: number } = jwtDecode(token);
            const now = Math.floor(Date.now() / 1000);
            return decoded.exp > now;
        } catch {
            return false;
        }
    },
};

// 导出token检查工具函数
export function checkIfTokenExpired(token: string): boolean {
    try {
        const decoded: { exp: number } = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);
        return decoded.exp < now;
    } catch {
        return true;
    }
}
